import { useState } from 'react'
import { format } from 'date-fns'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import TableCellWithCopy from '@/components/TableCellWithCopy'
import { useTranslation } from 'react-i18next'
import { useGetCameraListsQuery } from '@/api/camera'
import TableHeader from '@/components/TableHeader'
import { cameraDataHead } from '../../../../utils/stub'
import DialogForm from './dialog-form'
import Stack from '@mui/material/Stack'
import SearchInput from '@/components/SearchInput'

const Camera = () => {
  const { t } = useTranslation()
  const { data, isSuccess, isError, isLoading } = useGetCameraListsQuery()
  const [search, setSearch] = useState()
  const [orderBy, setOrderBy] = useState('number')
  const [order, setOrder] = useState('asc')
  const [currentId, setCurrentId] = useState()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [openModal, setOpenModal] = useState(false)
  const [filterStatus, setFilterStatus] = useState(null)

  const cameras = data
    ?.filter((camera) =>
        String(camera.description)
          ?.toLowerCase()
          .search(search?.toLowerCase()) >= 0 || camera?.id === search
    )
    ?.filter((camera) =>
      filterStatus !== null ? camera.is_active === filterStatus : camera
    ) ?? []

  const handleFilter = (status) => {
    setFilterStatus(status)
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleOpenModal = () => {
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setCurrentId()
  }

  const stableSort = (array, comparator) => {
    const stabilizedThis = array?.map((el, index) => [el, index])
    stabilizedThis?.sort((a, b) => {
      const orderTable = comparator(a[0], b[0])
      if (orderTable !== 0) {
        return orderTable
      }
      return a[1] - b[1]
    })
    return stabilizedThis?.map((el) => el[0])
  }

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1
    }
    if (b[orderBy] > a[orderBy]) {
      return 1
    }
    return 0
  }

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy)
  }

  const handleEdit = (id) => () => {
    setCurrentId(id)
    handleOpenModal()
  }

  return (
    <Stack
      direction="column"
      spacing={2}
    >
      <Stack direction="row" spacing={2}>
        <SearchInput 
          placeholder={t('enter_id_or_name')}
          onChange={(value) => setSearch(value)}
          delay={300}
        />
        <Button
          color="primary"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleOpenModal}
        >
          {t('add')}
        </Button>
      </Stack>
      <TableContainer>
        <Table>
          <TableHeader
            order={order}
            orderBy={orderBy}
            headLabel={cameraDataHead}
            rowCount={cameras?.length}
            onRequestSort={handleRequestSort}
          />
            <TableBody>
              {
                stableSort(cameras, getComparator(order, orderBy))
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                ?.map((camera, idx) => {
                  const labelId = `enhanced-table-checkbox-${idx}`
                  return (
                    <TableRow
                      hover
                      key={camera?.id}
                      tabIndex={-1}
                    >
                      <TableCellWithCopy copyText={camera?.id}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            width: "70px",
                            display: "block",
                            overflow: "hidden",
                            cursor: "default",
                          }}
                          title={camera?.id}
                          >
                          {camera?.id}
                        </Typography>
                      </TableCellWithCopy>
                      <TableCell>
                        <Typography variant="subtitle2" noWrap>
                          {camera.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" noWrap>
                          {camera.mac_address}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" noWrap>
                          {camera.ip_address}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" noWrap>
                        {camera.direction}
                        </Typography>
                      </TableCell>
                      <TableCellWithCopy 
                        copyText={camera.channel_id}
                      >
                        <Typography 
                          variant="subtitle2" 
                          sx={{
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            width: "70px",
                            display: "block",
                            overflow: "hidden",
                            cursor: "default",
                          }}
                          title={camera.channel_id}
                          >
                          {camera.channel_id}
                        </Typography>
                      </TableCellWithCopy>
                      <TableCell>
                        <Typography>
                          {format(new Date(camera.created_at), 'dd.MM.yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t('edit')}>
                          <IconButton
                            sx={{ mr: 2 }}
                            id="menu"
                            size="small"
                            aria-label="edit"
                            onClick={handleEdit(camera.id)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[ 10, 25 ]}
          component="div"
          count={cameras?.length || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {
          isSuccess && data?.length === 0 && (
            <Alert variant="filled" severity="warning">
              {t('no_matching_cameras_found.')}
            </Alert>
          )
        }
      <DialogForm
        open={openModal}
        onClose={handleCloseModal}
        currentId={currentId}
      />
    </Stack>
  )
}

export default Camera